/**
 * Was die Relevanzanzeige im Verlauf ZUSAGT -- an EINER Stelle.
 *
 * Dieselbe Konvention wie shortcuts.js (KUERZEL), hinweise.js (NEU_BEWERTEN),
 * merkliste.js (MERK_MAX) und promptMass.js (PROMPT_WARN_ZEICHEN): eine
 * Aussage bekommt einen NAMEN, der Wortlaut daneben ist frei und darf
 * jederzeit umformuliert werden. Ein Schlüssel verschwindet nur, wenn die
 * Aussage selbst verschwindet — der Test prüft Schlüsselmenge, Bindung und
 * Zahlen, nie die Prosa.
 *
 * ⚠️ Warum das hier besonders zählt: die drei Zustände unten sind KEINE
 * Farbabstufungen derselben Sache. „kein Urteil" und „nicht relevant" sind
 * verschiedene Aussagen, und die Karte muss sie unterscheidbar zeigen —
 * andernfalls liest sich eine vom Modell übersprungene Zeile wie eine
 * abgelehnte.
 */

/**
 * Die drei Zustände einer Verlaufszeile.
 *
 * ohne       kein Urteil erhoben, oder das Modell hat diese Zeile
 *            übersprungen. Keine Fläche, kein Schalterzustand — nichts
 *            behaupten.
 * relevant   gehört zum selben Kommunikationsstrang.
 * fremd      gehört nicht dazu.
 *
 * ⚠️ ACHTUNG, hier stand bis zum 20.08. ein VIERTER Eintrag „korrigiert“.
 * Das war falsch und irreführend: eine Korrektur ist keine vierte Ausprägung
 * neben den drei oben, sondern eine ANDERE Achse. `relevanzZustand()` kann
 * `korrigiert` gar nicht zurückgeben — es liest `relevance.effective`, und das
 * ist true, false oder nichts. Ob ein Mensch eingegriffen hat, steht in
 * `human_value` und wird daneben angezeigt, nicht als Zustand.
 *
 * ⚠️ Die Fläche allein darf die Aussage nicht tragen (WCAG 1.4.1). Jeder
 * Zustand führt deshalb einen `text`, der im Schalter sichtbar steht — nicht
 * nur im Tooltip, denn der erscheint an einem Bedienelement erst beim
 * Hovern und an einem gesperrten gar nicht.
 */
export const RELEVANZ = {
  ohne: {
    text: "?",
    titel: "Noch kein Urteil über diese Zeile",
    flaeche: "",
  },
  relevant: {
    text: "relevant",
    titel: "Gehört zum selben Gesprächsstrang",
    // ⚠️ bg-*, nicht border-*: der Rand trägt die HERKUNFT (border-l-*), und
    // zwei Aussagen auf demselben Pixel gewinnen zufällig. Die Aufteilung
    // steht seit U3 (11.08.) im Template und ist testgesichert.
    flaeche: "bg-emerald-50",
  },
  fremd: {
    text: "nicht relevant",
    titel: "Gehört zu einem anderen Gesprächsstrang",
    flaeche: "bg-gray-100",
  },
};

/**
 * Die FESTE Breite der Schalterspalte einer Verlaufszeile.
 *
 * ⚠️ Ohne sie wandert beim Umschalten der NACHRICHTENTEXT: die drei Texte
 * oben sind verschieden breit („?" gegen „nicht relevant"), die Spalte ist
 * `shrink-0`, und was sie mehr belegt, nimmt sie dem Text daneben — der
 * bricht dann anders um. Wer eine Zeile umschaltet, ändert ihr Urteil,
 * nicht ihren Satzspiegel; ein springender Text liest sich wie ein Fehler.
 *
 * Der Wert trägt den längsten Text (`fremd`) bei `text-[11px]` samt
 * Innenabstand und Rahmen — und er ist die KLEINSTE Stufe, die das tut:
 * `w-20` sind 80 px, Innenraum 78, der Text braucht 70. `w-16` (64 px,
 * Innenraum 62) läuft über — gemessen, scrollWidth 70 gegen clientWidth 62.
 * Ohne die feste Breite schwankt der Knopf zwischen 14,94 und 75,75 px und
 * der Nachrichtentext daneben um 60,8 px. Wird ein Text länger, wächst diese
 * Zahl mit — und dann ist sie WIEDER die kleinste passende, nicht die
 * bequemste.
 *
 * ⚠️ Die Klasse muss WÖRTLICH hier stehen: Tailwind scannt diese Datei
 * (`content: ./src/**\/*.{vue,js,…}`) und erzeugt nur, was es als Zeichenkette
 * findet — ein zusammengesetzter Name (`w-${n}`) ergäbe keine Regel.
 */
export const SCHALTER_BREITE = "w-20";

/**
 * Was am Kopf des Verlaufs steht, während und nachdem geprüft wurde.
 *
 * ⚠️ `laeuft` ist kein Schmuck: der Lauf dauert. Drüben startet ein Modell,
 * ein Kaltstart kostet gemessen rund 68 s, das gui-backend wartet bis zu
 * 300 s. Ohne sichtbaren Zustand sieht ein laufender Aufruf genauso aus wie
 * ein Knopf, der nicht reagiert hat.
 */
export const LAUF = {
  knopf: "Relevanz prüfen",
  laeuft: "Relevanz wird geprüft …",
  erneut: "Erneut prüfen",
  ohneVerlauf: "Ohne Verlauf gibt es nichts zu beurteilen",
  erhoben: "Relevanz erhoben",
  nichtErhoben: "Relevanz noch nicht erhoben",
};

/**
 * Die Sätze, die eine unvollständige oder unbrauchbare Antwort erklären.
 *
 * ⚠️ Diese vier sind der Grund, warum der Lauf überhaupt einen Bericht
 * zurückgibt. Ein Modell, das zwei von fünf Zeilen überspringt, liefert ein
 * Ergebnis, das wie ein vollständiges aussieht — der Unterschied steht nur
 * hier.
 */
export const BEFUND = {
  unbrauchbar: "Die Antwort des Modells war nicht auswertbar",
  uebersprungen: "Zeilen ohne Urteil",
  waisen: "Urteile zu Zeilen, die heute nicht mehr im Fenster liegen",
  ohneId: "Zeilen ohne Kennung — für sie lässt sich kein Urteil ablegen",
};

/**
 * Der Zustand einer Zeile aus ihrem Urteil.
 *
 * Genau EINE Stelle entscheidet das; die Karte fragt nicht selbst nach
 * `human_value ?? model_value`. Sonst stünde dieselbe Regel zweimal im Baum
 * — Frontend und Backend führen sie ohnehin schon je einmal
 * (MessageContextRelevance.effective_value und _als_relevanz).
 */
export function relevanzZustand(relevance) {
  if (!relevance) return "ohne";
  const gilt = relevance.effective;
  if (gilt === true) return "relevant";
  if (gilt === false) return "fremd";
  return "ohne";
}

/**
 * Die zwei Gründe, aus denen eine Zeile kein Urteil trägt.
 *
 * ⚠️ Seit der Hintergrundlauf existiert (19.08.), ist „?" nicht mehr
 * eindeutig. Vorher gab es nur einen Weg zum Urteil — den Knopf —, und wer
 * ihn nicht gedrückt hatte, wusste warum. Jetzt fallen zwei verschiedene
 * Sachverhalte auf dasselbe Zeichen:
 *
 *   ohne `ran_at`  für diese Nachricht wurde noch gar nichts erhoben.
 *   mit  `ran_at`  ein Lauf hat stattgefunden und diese Zeile ausgelassen.
 *
 * Der zweite Fall ist der interessante: ein Modell, das Zeilen überspringt,
 * liefert ein Ergebnis, das wie ein vollständiges aussieht. Genau dagegen ist
 * `model_value` nullable — und die Karte darf den Unterschied nicht wieder
 * einebnen.
 *
 * ⚠️ Was hier bewusst NICHT steht: „der Hintergrundlauf holt sie nach". Die
 * Karte weiß nicht, ob der Worker eingeschaltet ist; das wäre ein Versprechen
 * ohne Deckung.
 */
export const OHNE_URTEIL = {
  nichtGelaufen: "Für diese Nachricht wurde noch keine Relevanz erhoben",
  uebersprungen: "Der Lauf hat zu dieser Zeile nichts gesagt",
};

export function ohneTitel(ranAt) {
  return ranAt ? OHNE_URTEIL.uebersprungen : OHNE_URTEIL.nichtGelaufen;
}
